import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { updateCreationSettings } from "../../../store/slices/projectSlice";
import { Users, Shield, Clock, DollarSign } from "lucide-react";

const ProjectSettings = () => {
  const dispatch = useDispatch();
  const settings = useSelector((state) => state.project.creation.settings);

  const handleSettingChange = (field, value) => {
    dispatch(updateCreationSettings({ [field]: value }));
  };

  return (
    <div className="fade-in">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Project Settings
        </h2>
        <p className="text-gray-600">
          Configure final settings for your project
        </p>
      </div>

      <div className="space-y-6">
        {/* Worker Settings */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Users className="w-5 h-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Worker Settings
            </h3>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Maximum Number of Workers
              </label>
              <input
                type="number"
                value={settings.maxWorkers}
                onChange={(e) =>
                  handleSettingChange(
                    "maxWorkers",
                    parseInt(e.target.value) || 0,
                  )
                }
                min="1"
                max="1000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Enter maximum workers"
              />
              <p className="text-sm text-gray-600 mt-1">
                Limit the number of workers who can participate in this project
              </p>
            </div>
          </div>
        </div>

        {/* Quality Settings */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-5 h-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Quality Control
            </h3>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-900">
                  Auto-approve submissions
                </label>
                <p className="text-sm text-gray-600">
                  Automatically approve work without manual review
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.autoApprove}
                  onChange={(e) =>
                    handleSettingChange("autoApprove", e.target.checked)
                  }
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-900">
                  Require approval for submissions
                </label>
                <p className="text-sm text-gray-600">
                  All submissions must be manually reviewed before approval
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.requireApproval}
                  onChange={(e) =>
                    handleSettingChange("requireApproval", e.target.checked)
                  }
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>
          </div>
        </div>

        {/* Payment Settings */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <DollarSign className="w-5 h-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Payment Settings
            </h3>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment per Task (USD)
              </label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={settings.paymentPerTask || ""}
                onChange={(e) =>
                  handleSettingChange(
                    "paymentPerTask",
                    parseFloat(e.target.value) || 0,
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="0.10"
              />
              <p className="text-sm text-gray-600 mt-1">
                Amount to pay workers for each completed task
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bonus for Quality Work (USD)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={settings.qualityBonus || ""}
                onChange={(e) =>
                  handleSettingChange(
                    "qualityBonus",
                    parseFloat(e.target.value) || 0,
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="0.05"
              />
              <p className="text-sm text-gray-600 mt-1">
                Additional payment for high-quality submissions
              </p>
            </div>
          </div>
        </div>

        {/* Time Settings */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Clock className="w-5 h-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Time Settings
            </h3>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text sm font-medium text-gray-700 mb-2">
                Time Limit per Task (minutes)
              </label>
              <input
                type="number"
                min="1"
                value={settings.timeLimit || ""}
                onChange={(e) =>
                  handleSettingChange(
                    "timeLimit",
                    parseInt(e.target.value) || 0,
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="30"
              />
              <p className="text-sm text-gray-600 mt-1">
                Maximum time workers have to complete each task
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project Deadline
              </label>
              <input
                type="datetime-local"
                value={settings.deadline || ""}
                onChange={(e) =>
                  handleSettingChange("deadline", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <p className="text-sm text-gray-600 mt-1">
                When the project should be completed
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectSettings;
